import { ReactElement } from "react";
import styles from "./anchorblock.module.css";
import ResponsiveImage from "../../Element/ResponsiveImage";
import { placeholderCrops } from "../../Utility/placeholder";
import { DateTime } from "../../Element/Typography";
import cx from "classnames";
import BreadCrumbs from "../../Element/BreadCrumb";
import { SummaryItemLarge } from "../../Block/SummaryItem/SummaryItem";
import { BaseTeaserData, ImageCrop } from "../../Utility/dataTypes";
import Divider from "../../Element/Divider/divider";
import { useCustomClassContext } from "../../../hooks/useCustomClassContext/CustomClassContext";

export type AnchorBlockProps = BaseTeaserData & {
  loading?: "lazy" | "eager";
  displayType?: "row" | "column";
  placeholder?: ImageCrop;
  displayPublishDate?: boolean;
  CustomHeadingElement?: React.ComponentProps<
    typeof SummaryItemLarge
  >["CustomHeadingElement"];
  hideBreadCrumb?: boolean;
  hideVerticalLine?: boolean;
  mobileCrop?: "square_30_26" | "landscape" | "portrait";
  aspectRatio?: number;
};

/**
 * This is our main Anchor-block component
 */
function AnchorBlock({
  publishDate,
  title,
  path,
  sections,
  image,
  loading,
  displayType = "column",
  placeholder = placeholderCrops.landscape,
  displayPublishDate = true,
  CustomHeadingElement,
  hideBreadCrumb: hideBreadCrumb,
  hideVerticalLine: hideVerticalLine,
  mobileCrop = "square_30_26",
  aspectRatio,
}: AnchorBlockProps): ReactElement {
  const customClass = useCustomClassContext();
  const isDisplayTypeRow = displayType === "row";
  const dateBeforeTitle = !isDisplayTypeRow;

  const beforeTitle = !hideBreadCrumb && (
    <BreadCrumbs
      sections={sections}
      dateElement={
        displayPublishDate &&
        dateBeforeTitle &&
        publishDate && (
          <DateTime date={new Date(publishDate)} format="dd mname yyyy" />
        )
      }
    />
  );

  const mobileCropImage = image?.crops[mobileCrop];
  const mobileImage = mobileCropImage || placeholder;

  const landscapeCrop = image?.crops.landscape;
  const mobileUpImage = landscapeCrop || placeholder;

  return (
    <section
      className={cx(
        styles.anchorBlock,
        isDisplayTypeRow && styles.displayRow,
        !hideVerticalLine && customClass?.anchorBlock?.verticalLine,
        customClass?.anchorBlock?.container,
      )}
    >
      <div className={styles.imageContainer}>
        <a href={path} title={title}>
          <ResponsiveImage
            mobileOnlyLqip={
              image?.crops.lqip?.[mobileCrop] || image?.crops.lqip?.landscape
            }
            displayWidth={1000}
            loading={loading}
            {...mobileImage}
            mobileUp={{ ...mobileUpImage, fillAspectRatio: aspectRatio }}
            errorSrc={placeholder.src}
            fillAspectRatio={aspectRatio}
          />
        </a>
      </div>

      <div
        className={cx(
          styles.summaryItemWrapper,
          !hideVerticalLine && customClass?.anchorBlock?.summaryItem,
        )}
      >
        <SummaryItemLarge
          beforeTitle={beforeTitle}
          title={title}
          url={path}
          publishedDate={!dateBeforeTitle ? publishDate : undefined}
          CustomHeadingElement={CustomHeadingElement}
        />
        <div className={styles.summaryItemDivider}>
          <Divider variant="secondary" />
        </div>
      </div>
    </section>
  );
}

export default AnchorBlock;
